import React, { useCallback, useEffect, useMemo } from 'react'
import {
  GhgConstants,
  LivestockBalanceItem,
  LivestockClass,
  LivestockClassType,
  LivestockMovementItem,
  LivestockTypeName,
} from 'api/models'
import { Form, Table, Alert } from 'react-bootstrap'
import { Control, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useGhgCalculatorFormContext, RegisterFormFn } from './use-ghg-calculator-form'
import { FormValuesWatcher } from './FormValuesWatcher'
import { GhgCalculatorNavigation } from './GhgCalculatorNavigation'
import { NumberField } from './base/form/NumberField'
import * as yup from 'yup'
import sumBy from 'lodash/sumBy'
import isEqual from 'lodash/isEqual'
import lodashGet from 'lodash/get'
import sortBy from 'lodash/sortBy'
import { useYupValidationResolver } from 'hooks/use-yup-validation-resolver'
import { yupNullableMinZeroInteger } from 'utils/validation'
import { coerceToNumber, formatNumber, formatPercentage } from 'utils/formatting'
import {
  useGrazingOffTally,
  useGrazingOnTally,
  useLossCalculation,
  useOnHandOpenCloseCalculation,
} from './use-ghg-calculation'
import {
  FarmDetailState,
  GrazingOffMovementItemFrontend,
  GrazingOnMovementItemFrontend,
} from 'components/ghg-calculator.state'
import { LivestockClassTooltip } from './base/LivestockClassTooltip'
import { getSeasonRange } from 'utils/season'
import './GhgLivestockMovementsStep.scss'
import Tippy from '@tippyjs/react'
import { TooltipIcon } from './base/TooltipIcon'

const LIVESTOCK_TYPE_BIRTH_CLASS_DISPLAY: Record<LivestockTypeName, string> = {
  SHEEP: 'Lamb',
  BEEF: 'Calf/Weaner',
  DAIRY: 'Calf/Weaner',
  DEER: 'Fawn/Weaner',
}

interface FormValues {
  items: Array<LivestockMovementItem>
}

const validationSchema = yup.object({
  items: yup.array().of(
    yup.object({
      births: yupNullableMinZeroInteger(),
      purchases: yupNullableMinZeroInteger(),
      sales_store: yupNullableMinZeroInteger(),
      sales_prime: yupNullableMinZeroInteger(),
      used_on_farm: yupNullableMinZeroInteger(),
    } as Record<keyof LivestockMovementItem, any>) // eslint-disable-line @typescript-eslint/no-explicit-any
  ),
})

const fieldLabels: { [K in keyof LivestockMovementItem]?: string } = {
  births: 'Births',
  purchases: 'Purchases',
  sales_store: 'Sales store',
  sales_prime: 'Sales prime',
}

export const GhgLivestockMovementsStep = () => {
  const {
    farmDetail,
    livestockBalances,
    livestockClasses,
    livestockClassTypes,
    livestockMovements,
    setLivestockMovements,
    grazingOnMovements,
    grazingOffMovements,
    ghgConstants,
    registerForm,
    negativeLossErrors,
  } = useGhgCalculatorFormContext()

  if (
    !(
      farmDetail &&
      livestockMovements &&
      livestockBalances &&
      livestockClasses &&
      livestockClassTypes &&
      ghgConstants
    )
  ) {
    return null
  }

  return (
    <GhgLivestockMovementsStepInner
      farmDetail={farmDetail}
      // Put the items in position order so the table will look right.
      livestockMovements={sortBy(
        livestockMovements,
        (lm) => livestockClasses.find((lc) => lc.id === lm.classification)?.position
      )}
      livestockBalances={livestockBalances}
      livestockClasses={livestockClasses}
      livestockClassTypes={livestockClassTypes}
      ghgConstants={ghgConstants}
      grazingOnMovements={grazingOnMovements}
      grazingOffMovements={grazingOffMovements}
      onLivestockMovementsChanged={setLivestockMovements}
      registerForm={registerForm}
      negativeLossErrors={negativeLossErrors}
    />
  )
}

const GhgLivestockMovementsStepInner = (props: {
  farmDetail: FarmDetailState
  livestockClasses: LivestockClass[]
  livestockClassTypes: LivestockClassType[]
  livestockMovements: LivestockMovementItem[]
  livestockBalances: LivestockBalanceItem[]
  grazingOnMovements: GrazingOnMovementItemFrontend[]
  ghgConstants: GhgConstants
  grazingOffMovements: GrazingOffMovementItemFrontend[]
  onLivestockMovementsChanged: (items: LivestockMovementItem[]) => void
  registerForm: RegisterFormFn
  negativeLossErrors?: null | { loss: { [K in LivestockTypeName]?: string } }
}) => {
  const {
    livestockClasses,
    livestockClassTypes,
    livestockMovements,
    livestockBalances,
    negativeLossErrors,
  } = props
  const {
    farmDetail,
    grazingOnMovements,
    grazingOffMovements,
    onLivestockMovementsChanged,
    registerForm,
  } = props

  const [seasonStart, seasonEnd] = useMemo(() => {
    if (!farmDetail.season_year) {
      throw new Error('[GhgLivestockMovementsStep] farmDetail.season_year should not be empty')
    }

    return getSeasonRange(farmDetail.season_year, farmDetail.end_month)
  }, [farmDetail])

  const { onHandAtOpen, onHandAtClose } = useOnHandOpenCloseCalculation(livestockBalances)

  const resolver = useYupValidationResolver(validationSchema)

  const formBag = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      items: livestockMovements,
    },
    shouldUnregister: false,
    resolver,
  })

  const { control, handleSubmit, formState } = formBag

  useEffect(() => {
    registerForm(formBag)
    return () => registerForm(null)
  }, [formBag, registerForm])

  // formState is wrapped with Proxy to improve render performance, so make sure you invoke or read it
  // before render in order to enable the state update.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { errors, touched } = formState

  const { fields: items } = useFieldArray<LivestockMovementItem>({
    control,
    name: 'items',
  })

  const handleValuesChanged = useCallback(
    ({ items }: { items: LivestockMovementItem[] }) => {
      onLivestockMovementsChanged(items)
    },
    [onLivestockMovementsChanged]
  )

  return (
    <Form>
      {formState.isSubmitted && Object.keys(errors).length > 0 && (
        <div className="mt-4 mb-4">
          <span className="text-danger">Please correct the errors below to continue.</span>
        </div>
      )}
      <Alert variant="info">
        If animals open in a livestock class, they need to be sold within the same livestock class –
        e.g. hoggets at open need to be sold as hoggets, not lambs. If you record animals in a
        different class, your loss value and overall GHG calculation will be inaccurate. For animals
        retained, all animals should be aged up as in previous 'Livestock balances’ tab – e.g. lambs
        retained over balance date should show as hoggets at close, Heifers R2 become MA Cows.
        Losses are automatically calculated – if results show as negative it is likely due to an
        ageing up issue as above.
      </Alert>
      {livestockClassTypes.map((livestockType) => {
        const lossErrorMessage = lodashGet(negativeLossErrors?.loss, livestockType.name)
        return (
          <div key={livestockType.id} data-testid={`${livestockType.name}-table`}>
            <legend className="section-heading">{livestockType.name_display}</legend>
            <Table role="grid" className="table-livestock-movements table-custom table-responsive">
              <colgroup>
                <col />
                <col />
                <col />
                <col />
                <col span={2} />
                <col span={2} />
                <col span={2} />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th rowSpan={2}>Stock</th>
                  <th rowSpan={2} className="text-center">
                    On hand at open
                  </th>
                  <th rowSpan={2} className="text-center">
                    Births
                  </th>
                  <th rowSpan={2} className="text-center">
                    Purchases
                  </th>
                  <th colSpan={2} className="text-center">
                    Sales
                  </th>
                  <th colSpan={2} className="text-center text-nowrap-lg">
                    On-farm grazing
                  </th>
                  <th colSpan={2} className="text-center text-nowrap-lg">
                    Off-farm grazing
                  </th>
                  <th rowSpan={2} className="text-center">
                    <Tippy
                      interactive
                      content={
                        <>
                          This includes any home kills, dog tuckers or animals otherwise used on
                          farm rather than being sold
                        </>
                      }
                    >
                      <span>
                        Used on farm
                        <TooltipIcon />
                      </span>
                    </Tippy>
                  </th>
                  <th rowSpan={2} className="text-center">
                    On hand at close
                  </th>
                </tr>
                <tr>
                  <th className="text-center col-pair">Store</th>
                  <th className="text-center col-pair">Prime</th>
                  <th className="text-center col-pair">Arrive</th>
                  <th className="text-center col-pair">Leave</th>
                  <th className="text-center col-pair">Depart</th>
                  <th className="text-center col-pair">Return</th>
                </tr>
              </thead>
              <tbody>
                {items.map((movement, movementItemIdx) => {
                  const stockClass = livestockClasses.find(
                    (item) => item.id === movement.classification
                  )

                  if (!stockClass || stockClass.livestock_type !== livestockType.id) {
                    return null
                  }

                  const fieldPrefix = `items[${movementItemIdx}]`

                  return (
                    <LivestockMovementRow
                      key={stockClass.id}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      initialValue={movement as any}
                      control={control}
                      fieldPrefix={fieldPrefix}
                      stockClass={stockClass}
                      stockType={livestockType}
                      onHandAtOpen={onHandAtOpen}
                      onHandAtClose={onHandAtClose}
                      grazingOnMovements={grazingOnMovements}
                      grazingOffMovements={grazingOffMovements}
                      seasonStart={seasonStart}
                      seasonEnd={seasonEnd}
                      rowErrors={{ ...lodashGet(errors, fieldPrefix) }}
                      rowTouched={{ ...lodashGet(touched, fieldPrefix) }}
                      isSubmitted={formState.isSubmitted}
                    />
                  )
                })}
              </tbody>
              <tfoot>
                <LivestockMovementTotalRow
                  control={control}
                  livestockType={livestockType}
                  livestockClasses={livestockClasses}
                  onHandAtOpen={onHandAtOpen}
                  onHandAtClose={onHandAtClose}
                  grazingOnMovements={grazingOnMovements}
                  grazingOffMovements={grazingOffMovements}
                  seasonStart={seasonStart}
                  seasonEnd={seasonEnd}
                />
                <LivestockMovementTotalSalesRow
                  control={control}
                  livestockType={livestockType}
                  livestockClasses={livestockClasses}
                  livestockClassTypes={livestockClassTypes}
                  livestockBalances={livestockBalances}
                  grazingOnMovements={grazingOnMovements}
                  grazingOffMovements={grazingOffMovements}
                  seasonStart={seasonStart}
                  seasonEnd={seasonEnd}
                />
                {lossErrorMessage ? (
                  <tr>
                    <td colSpan={6}></td>
                    <td className="text-danger text-right" colSpan={6}>
                      {lossErrorMessage}
                    </td>
                  </tr>
                ) : null}
              </tfoot>
            </Table>
          </div>
        )
      })}
      <FormValuesWatcher control={control} onValuesChange={handleValuesChanged} />
      {formState.isSubmitted && Object.keys(errors).length > 0 && (
        <p className="text-right">
          <span className="text-danger">Please correct the errors above to continue.</span>
        </p>
      )}
      <GhgCalculatorNavigation handleSubmit={handleSubmit} />
    </Form>
  )
}

const LivestockMovementRow = React.memo(
  (props: {
    control: Control
    fieldPrefix: string
    initialValue: LivestockMovementItem
    stockType: LivestockClassType
    stockClass: LivestockClass
    onHandAtOpen: Record<number, number>
    onHandAtClose: Record<number, number>
    grazingOnMovements: GrazingOnMovementItemFrontend[]
    grazingOffMovements: GrazingOffMovementItemFrontend[]
    seasonStart: Date
    seasonEnd: Date

    // these following props are mainly for the purpose of React.memo optimization
    // so the components get re-render properly when these props changed
    rowErrors: any // eslint-disable-line @typescript-eslint/no-explicit-any
    rowTouched: any // eslint-disable-line @typescript-eslint/no-explicit-any
    isSubmitted: boolean
  }) => {
    const { control, fieldPrefix, initialValue } = props
    const { stockClass, stockType, onHandAtOpen, onHandAtClose } = props
    const { grazingOnMovements, grazingOffMovements, seasonStart, seasonEnd } = props

    const movement: LivestockMovementItem | undefined = useWatch({
      control,
      name: fieldPrefix,
    })

    const grazingOnTally = useGrazingOnTally(
      [movement?.classification ?? null],
      grazingOnMovements,
      seasonStart,
      seasonEnd
    )
    const grazingOffTally = useGrazingOffTally(
      [movement?.classification ?? null],
      grazingOffMovements,
      seasonStart,
      seasonEnd
    )

    if (!movement) {
      return null
    }

    return (
      <tr>
        <td>
          <LivestockClassTooltip
            livestockType={stockType.name}
            livestockClassName={stockClass.name}
          />
        </td>

        <td className="number">
          {stockClass.is_births_class
            ? null
            : formatNumber(onHandAtOpen[movement.classification], 0)}
        </td>
        {(['births', 'purchases', 'sales_store', 'sales_prime'] as Array<
          keyof LivestockMovementItem
        >).map((fieldName) => (
          <td
            className={
              fieldName === 'sales_store' || fieldName === 'sales_prime'
                ? 'number bg-light col-pair'
                : 'number bg-light'
            }
            key={fieldName}
          >
            {fieldName === 'births' && !stockClass.is_births_class ? null : (
              <NumberField
                name={`${fieldPrefix}.${fieldName}`}
                control={control}
                placeholder="0"
                data-testid={`${stockClass.name}.${fieldName}`}
                defaultValue={initialValue[fieldName]}
                maxLength={6}
                aria-label={fieldLabels[fieldName]}
              />
            )}
          </td>
        ))}
        <td className="text-center col-pair">{grazingOnTally.on || ''}</td>
        <td className="text-center col-pair">{grazingOnTally.off || ''}</td>
        <td className="text-center col-pair">{grazingOffTally.off || ''}</td>
        <td className="text-center col-pair">{grazingOffTally.on || ''}</td>
        <td className="number bg-light">
          <NumberField
            name={`${fieldPrefix}.used_on_farm`}
            control={control}
            placeholder="0"
            data-testid={`${stockClass.name}.used_on_farm`}
            defaultValue={initialValue.used_on_farm}
            maxLength={6}
            aria-label="Used on farm"
          />
        </td>
        <td className="number">
          {stockClass.is_births_class
            ? null
            : formatNumber(onHandAtClose[movement.classification], 0)}
        </td>
      </tr>
    )
  },
  (prevProps, nextProps) =>
    isEqual(nextProps.rowErrors, prevProps.rowErrors) &&
    isEqual(nextProps.rowTouched, prevProps.rowTouched) &&
    nextProps.isSubmitted === prevProps.isSubmitted
)

const LivestockMovementTotalRow = React.memo(
  (props: {
    control: Control
    livestockType: LivestockClassType
    livestockClasses: LivestockClass[]
    onHandAtOpen: Record<number, number>
    onHandAtClose: Record<number, number>
    grazingOnMovements: GrazingOnMovementItemFrontend[]
    grazingOffMovements: GrazingOffMovementItemFrontend[]
    seasonStart: Date
    seasonEnd: Date
  }) => {
    const { control, livestockType, livestockClasses, onHandAtOpen, onHandAtClose } = props
    const { grazingOnMovements, grazingOffMovements, seasonStart, seasonEnd } = props

    const items: LivestockMovementItem[] = useWatch({
      control: control,
      name: 'items',
    }) as any // eslint-disable-line @typescript-eslint/no-explicit-any

    const movementItems = items.filter((movementItem) => {
      const stockClass = livestockClasses.find((clz) => clz.id === movementItem.classification)
      return stockClass && stockClass.livestock_type === livestockType.id
    })

    const grazingOnTally = useGrazingOnTally(
      movementItems.map((item) => item.classification),
      grazingOnMovements,
      seasonStart,
      seasonEnd
    )
    const grazingOffTally = useGrazingOffTally(
      movementItems.map((item) => item.classification),
      grazingOffMovements,
      seasonStart,
      seasonEnd
    )

    const totalOnHandAtOpen = sumBy(
      movementItems.map((movementItem) => onHandAtOpen[movementItem.classification])
    )
    const totalOnHandAtClose = sumBy(
      movementItems.map((movementItem) => onHandAtClose[movementItem.classification])
    )

    return (
      <tr className="total">
        <td className="text-uppercase">Total</td>
        <td className="number">{formatNumber(totalOnHandAtOpen, 0)}</td>

        {(['births', 'purchases', 'sales_store', 'sales_prime'] as Array<
          keyof LivestockMovementItem
        >).map((fieldName) => (
          <td
            className={
              fieldName === 'sales_store' || fieldName === 'sales_prime'
                ? 'number bg-light col-pair'
                : 'number bg-light'
            }
            key={fieldName}
          >
            {formatNumber(
              sumBy(movementItems, (item) => coerceToNumber(item[fieldName])),
              0
            )}
          </td>
        ))}
        <td className="text-center col-pair">{grazingOnTally.on || ''}</td>
        <td className="text-center col-pair">{grazingOnTally.off || ''}</td>
        <td className="text-center col-pair">{grazingOffTally.off || ''}</td>
        <td className="text-center col-pair">{grazingOffTally.on || ''}</td>
        <td className="number bg-light">
          {formatNumber(
            sumBy(movementItems, (item) => coerceToNumber(item.used_on_farm)),
            0
          )}
        </td>
        <td className="number">{formatNumber(totalOnHandAtClose, 0)}</td>
      </tr>
    )
  }
)

const LivestockMovementTotalSalesRow = React.memo(
  (props: {
    control: Control
    livestockType: LivestockClassType
    livestockClasses: LivestockClass[]
    livestockClassTypes: LivestockClassType[]
    livestockBalances: LivestockBalanceItem[]
    grazingOnMovements: GrazingOnMovementItemFrontend[]
    grazingOffMovements: GrazingOffMovementItemFrontend[]
    seasonStart: Date
    seasonEnd: Date
  }) => {
    const {
      control,
      livestockType,
      livestockClasses,
      livestockBalances,
      livestockClassTypes,
    } = props
    const { grazingOnMovements, grazingOffMovements, seasonStart, seasonEnd } = props
    const items: LivestockMovementItem[] = useWatch({
      control: control,
      name: 'items',
    }) as any // eslint-disable-line @typescript-eslint/no-explicit-any

    const losses = useLossCalculation(
      livestockBalances,
      items,
      grazingOnMovements,
      grazingOffMovements,
      livestockClasses,
      livestockClassTypes,
      seasonStart,
      seasonEnd
    )

    const loss = losses[livestockType.name]

    // find all the movementItems per the livestockType
    const effectiveMovementItems = items.filter((movementItem) => {
      const stockClass = livestockClasses.find((clz) => clz.id === movementItem.classification)
      return stockClass && stockClass.livestock_type === livestockType.id
    })

    return (
      <tr>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td colSpan={2} className="text-center text-uppercase col-pair-spanned">
          Total sales:{' '}
          {formatNumber(
            sumBy(effectiveMovementItems, (item) => coerceToNumber(item.sales_store)) +
              sumBy(effectiveMovementItems, (item) => coerceToNumber(item.sales_prime)),
            0
          )}
        </td>
        <td colSpan={6}>
          <div className="d-flex flex-row justify-content-end">
            <span className="text-uppercase">Losses:</span>
            <div className="ml-2">
              {loss.adultInput > 0 && (
                <span>
                  Adult: {formatPercentage(losses[livestockType.name].adultPctg, 1)} (
                  {losses[livestockType.name].adultHeads} head)
                </span>
              )}
              {loss.babyInput > 0 && (
                <span>
                  <br />
                  {LIVESTOCK_TYPE_BIRTH_CLASS_DISPLAY[livestockType.name]}:{' '}
                  {formatPercentage(losses[livestockType.name].babyPctg, 1)} (
                  {losses[livestockType.name].babyHeads} head)
                </span>
              )}
            </div>
          </div>
        </td>
      </tr>
    )
  }
)
